<div class="section-divider d-none d-md-flex align-items-center gap-3 mb-2">
  <div class="flex-grow-1 border-top"></div>
  <span class="fw-semibold small text-uppercase text-muted px-1" style="letter-spacing:.08em;">⚡ Energie</span>
  <div class="flex-grow-1 border-top"></div>
</div>

<!-- Live Solar Cards -->
<div class="section-label d-flex align-items-center gap-2 mb-2">
  <span class="badge bg-danger" style="font-size:.7rem;letter-spacing:.05em;"><span [style.opacity]="liveDotOn ? '1' : '0'">●</span> LIVE</span>
  <span class="fw-semibold small text-uppercase tracking-wide">{{ 'AKTUELL' | translate }}</span>
  <span *ngIf="solarUpdatedAt" class="text-muted" style="font-size:.65rem;">· {{ 'UPDATED_AGO' | translate:{time: updatedAgo(solarUpdatedAt)} }}</span>
  <button *ngIf="solarHistory.length" class="btn btn-sm btn-outline-secondary ms-auto" style="font-size:.7rem;padding:1px 8px;" (click)="openSolarChart.emit()">📈 24h</button>
</div>

<div class="row g-2 g-sm-3 mb-3" *ngIf="solar" [class.section-flash]="solarFlash">

  <div class="col-6 col-xl">
    <div class="card h-100 border-warning">
      <div class="card-body d-flex align-items-center gap-2 p-2 p-sm-3">
        <div class="card-icon fs-2 flex-shrink-0">☀️</div>
        <div class="min-w-0">
          <div class="text-muted small">{{ 'PV_ERTRAG' | translate }}</div>
          <div class="card-value fs-5 fw-bold text-warning text-truncate">{{ formatW(solar.pvYieldW) }}</div>
          <div *ngIf="vs.sunrise" class="text-muted d-none d-xl-block" style="font-size:.65rem;line-height:1.4;">
            🌅 {{ vs.sunrise }} &nbsp;|&nbsp; 🌇 {{ vs.sunset }}
          </div>
        </div>
      </div>
      <div *ngIf="vs.sunrise" class="card-footer d-xl-none text-muted text-center p-1" style="font-size:.62rem;">
        🌅 {{ vs.sunrise }} &nbsp;|&nbsp; 🌇 {{ vs.sunset }}
      </div>
    </div>
  </div>

  <div class="col-6 col-xl">
    <div class="card h-100 border-success">
      <div class="card-body d-flex align-items-center gap-2 p-2 p-sm-3">
        <div class="card-icon fs-2 flex-shrink-0"
             [class.battery-charging]="solar.batteryDischargeW < 0"
             [class.battery-discharging]="solar.batteryDischargeW > 0">
          <ng-container *ngIf="solar.batteryDischargeW < 0">⚡</ng-container>
          <ng-container *ngIf="solar.batteryDischargeW > 0">🪫</ng-container>
          <ng-container *ngIf="solar.batteryDischargeW === 0">🔋</ng-container>
        </div>
        <div class="flex-grow-1 min-w-0">
          <div class="text-muted small">{{ 'AKKU' | translate }}</div>
          <div class="card-value fs-5 fw-bold text-truncate"
               [class.text-success]="solar.batteryDischargeW <= 0"
               [class.text-danger]="solar.batteryDischargeW > 0">
            {{ formatW(-solar.batteryDischargeW) }}
          </div>
          <div class="progress mt-1" style="height:5px;">
            <div class="progress-bar"
                 [class.bg-success]="solar.batteryDischargeW === 0"
                 [class.progress-bar-charging]="solar.batteryDischargeW < 0"
                 [class.progress-bar-discharging]="solar.batteryDischargeW > 0"
                 [style.width.%]="solar.batteryPercent"></div>
          </div>
          <div class="text-muted mt-1 text-truncate" style="font-size:.72rem;">
            {{ solar.batteryPercent }} %
            <ng-container *ngIf="solar.batteryCapacityWh">
              &nbsp;·&nbsp;{{ (solar.batteryPercent / 100 * solar.batteryCapacityWh / 1000).toFixed(1) }}&thinsp;/&thinsp;{{ (solar.batteryCapacityWh / 1000).toFixed(0) }}&thinsp;kWh
            </ng-container>
            <ng-container *ngIf="batteryRuntime(solar) as rt">
              <span class="d-none d-xl-inline">&nbsp;·&nbsp;{{ rt }}</span>
            </ng-container>
          </div>
        </div>
      </div>
      <div *ngIf="batteryRuntime(solar) as rt" class="card-footer d-xl-none text-muted text-center p-1" style="font-size:.62rem;">{{ rt }}</div>
    </div>
  </div>

  <div class="col-6 col-xl">
    <div class="card h-100 border-info">
      <div class="card-body d-flex align-items-center gap-2 p-2 p-sm-3">
        <div class="card-icon fs-2 flex-shrink-0">🔌</div>
        <div class="min-w-0">
          <div class="text-muted small">{{ 'NETZBEZUG' | translate }}</div>
          <div class="card-value fs-5 fw-bold text-truncate"
               [class.text-danger]="solar.gridConsumptionW > 0"
               [class.text-success]="solar.gridConsumptionW <= 0">
            {{ formatW(solar.gridConsumptionW) }}
          </div>
          <div class="text-muted small">{{ (solar.gridConsumptionW > 0 ? 'BEZUG' : 'EINSPEISUNG') | translate }}</div>
        </div>
      </div>
    </div>
  </div>

  <div class="col-6 col-xl">
    <div class="card h-100 border-primary">
      <div class="card-body d-flex align-items-center gap-2 p-2 p-sm-3">
        <div class="card-icon fs-2 flex-shrink-0">🏠</div>
        <div class="min-w-0">
          <div class="text-muted small">{{ 'VERBRAUCH' | translate }}</div>
          <div class="card-value fs-5 fw-bold text-primary text-truncate">{{ formatW(solar.totalConsumptionW) }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Today's autarky summary -->
  <div class="col-6 col-xl" *ngIf="todayStat">
    <div class="card h-100" [class.border-success]="todayStat.autarkiePercent >= 80"
         [class.border-warning]="todayStat.autarkiePercent >= 50 && todayStat.autarkiePercent < 80"
         [class.border-danger]="todayStat.autarkiePercent < 50">
      <div class="card-body d-flex align-items-center gap-2 p-2 p-sm-3">
        <div class="card-icon fs-2 flex-shrink-0">🏡</div>
        <div class="min-w-0 flex-grow-1">
          <div class="d-flex align-items-baseline gap-1">
            <div class="text-muted small">{{ 'HEUTE_AUTARKIE' | translate }}</div>
            <div class="text-muted" style="font-size:.6rem;">{{ 'HEUTE_SEIT' | translate }}</div>
          </div>
          <div class="card-value fs-5 fw-bold text-truncate"
               [class.text-success]="todayStat.autarkiePercent >= 80"
               [class.text-warning]="todayStat.autarkiePercent >= 50 && todayStat.autarkiePercent < 80"
               [class.text-danger]="todayStat.autarkiePercent < 50">
            {{ todayStat.autarkiePercent.toFixed(0) }}&thinsp;%
          </div>
          <div class="text-muted text-truncate d-none d-xl-block" style="font-size:.68rem;line-height:1.5;">
            ⚡&thinsp;{{ formatKwh(todayStat.consumptionKwh) }}
            &nbsp;·&nbsp;🔌&thinsp;{{ formatKwh(todayStat.gridImportKwh) }}
          </div>
        </div>
      </div>
      <div class="card-footer d-xl-none text-muted text-center p-1" style="font-size:.62rem;">
        ⚡&thinsp;{{ formatKwh(todayStat.consumptionKwh) }} &nbsp;·&nbsp; 🔌&thinsp;{{ formatKwh(todayStat.gridImportKwh) }}
      </div>
    </div>
  </div>

</div>

<div *ngIf="!solar" class="text-center text-muted py-4">
  <div class="spinner-border spinner-border-sm me-2"></div>{{ 'LADE_SOLARDATEN' | translate }}
</div>
